/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API HTTP Status Donut Chart Component (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { PieChart, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { useDeveloperAnalyticsStore } from '../../../stores/useDeveloperAnalyticsStore';

export const HttpStatusDonutChart: React.FC = () => {
  const { t } = useTranslation();
  const { getHttpStatusBreakdown } = useDeveloperAnalyticsStore();

  const data = getHttpStatusBreakdown();
  const total = data.total || 1;

  // Calculate percentages
  const pct200 = (data.status200 / total) * 100;
  const pct429 = (data.status429 / total) * 100;
  const pct5xx = (data.status5xx / total) * 100;

  // Circumference for r = 42
  const r = 42;
  const circumference = 2 * Math.PI * r; // ~263.89

  // Stroke dash calculations
  const stroke200 = (pct200 / 100) * circumference;
  const stroke429 = (pct429 / 100) * circumference;
  const stroke5xx = (pct5xx / 100) * circumference;

  const offset200 = 0;
  const offset429 = -stroke200;
  const offset5xx = -(stroke200 + stroke429);

  return (
    <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 flex flex-col justify-between">
      {/* Header */}
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <PieChart className="size-4 text-emerald-500" />
          <h3 className="text-sm font-bold font-heading text-foreground">
            {t('apikeys.chartHttpStatus')}
          </h3>
        </div>
        <p className="text-xs text-foreground-muted">
          {t('apikeys.chartHttpStatusDesc')}
        </p>
      </div>

      {/* Donut Visualizer and Metrics */}
      <div className="flex flex-col sm:flex-row items-center justify-around gap-6 py-2">
        {/* SVG Donut */}
        <div className="relative size-36 shrink-0 flex items-center justify-center">
          <svg className="size-full transform -rotate-90" viewBox="0 0 100 100">
            {/* Background ring */}
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="transparent"
              stroke="currentColor"
              className="text-surface-border/40"
              strokeWidth="10"
            />

            {/* 200 OK segment */}
            {pct200 > 0 && (
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="transparent"
                stroke="#10b981"
                strokeWidth="10"
                strokeDasharray={`${stroke200} ${circumference}`}
                strokeDashoffset={offset200}
                className="transition-all duration-500"
              />
            )}

            {/* 429 Rate Limit segment */}
            {pct429 > 0 && (
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="transparent"
                stroke="#f59e0b"
                strokeWidth="10"
                strokeDasharray={`${stroke429} ${circumference}`}
                strokeDashoffset={offset429}
                className="transition-all duration-500"
              />
            )}

            {/* 5xx Error segment */}
            {pct5xx > 0 && (
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="transparent"
                stroke="#f43f5e"
                strokeWidth="10"
                strokeDasharray={`${stroke5xx} ${circumference}`}
                strokeDashoffset={offset5xx}
                className="transition-all duration-500"
              />
            )}
          </svg>

          {/* Center Text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-lg font-extrabold font-heading text-foreground">
              {data.successRate}%
            </span>
            <span className="text-[10px] text-foreground-muted font-medium">
              Success
            </span>
          </div>
        </div>

        {/* Legend List */}
        <div className="space-y-2.5 w-full sm:w-auto">
          {/* 200 OK */}
          <div className="flex items-center justify-between sm:justify-start gap-4 p-2 rounded-xl bg-surface-elevated border border-surface-border/60">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-emerald-500" />
              <div>
                <p className="text-xs font-semibold text-foreground">200 OK</p>
                <p className="text-[10px] text-foreground-muted">Success</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-foreground">
                {data.status200.toLocaleString()}
              </span>
              <span className="text-[10px] text-emerald-500 font-semibold block">
                {pct200.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 429 Rate Limited */}
          <div className="flex items-center justify-between sm:justify-start gap-4 p-2 rounded-xl bg-surface-elevated border border-surface-border/60">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-500" />
              <div>
                <p className="text-xs font-semibold text-foreground">429 Rate Limited</p>
                <p className="text-[10px] text-foreground-muted">Threshold cap</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-foreground">
                {data.status429.toLocaleString()}
              </span>
              <span className="text-[10px] text-amber-500 font-semibold block">
                {pct429.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 5xx Errors */}
          <div className="flex items-center justify-between sm:justify-start gap-4 p-2 rounded-xl bg-surface-elevated border border-surface-border/60">
            <div className="flex items-center gap-2">
              <XCircle className="size-4 text-rose-500" />
              <div>
                <p className="text-xs font-semibold text-foreground">5xx Errors</p>
                <p className="text-[10px] text-foreground-muted">Upstream timeout</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-mono font-bold text-foreground">
                {data.status5xx.toLocaleString()}
              </span>
              <span className="text-[10px] text-rose-500 font-semibold block">
                {pct5xx.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
